import { type ActionType, type ProColumns, type ProTableProps } from "@ant-design/pro-components";
import { Button, Modal, Space, Tabs } from "antd";
import { type Key, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { AdminPickerWorkspace } from "./admin-picker-workspace";
import { ADMIN_LIST_PAGINATION_STANDARD } from "./admin-table";
import {
  usePickerRowDoubleClick,
  withPickerRowDoubleClick,
} from "./picker-row-double-click";

export const SHARED_ASSET_PICKER_SCOPES = ["mine", "shared_with_me", "public"] as const;

export type SharedAssetPickerScope = (typeof SHARED_ASSET_PICKER_SCOPES)[number];

type ControlledFilterValue = Array<string | number | boolean> | null;
type ControlledFilters = Record<string, ControlledFilterValue>;

type SharedAssetPickerModalProps<T extends { id: number | string }> = {
  open: boolean;
  actionRef: React.MutableRefObject<ActionType | undefined>;
  activeScope: SharedAssetPickerScope;
  allowedScopes?: readonly SharedAssetPickerScope[];
  onScopeChange: (scope: SharedAssetPickerScope) => void;
  scopeLabels: Record<SharedAssetPickerScope, ReactNode>;
  columns: readonly ProColumns<T>[];
  request: NonNullable<ProTableProps<T, Record<string, unknown>>["request"]>;
  onClose: () => void;
  title: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  footerExtra?: ReactNode;
  submitText?: ReactNode;
  cancelText?: ReactNode;
  onSubmit?: () => Promise<boolean> | boolean;
  width?: number | string;
  rowKey?: string | ((record: T) => string | number);
  selectedRowKeys?: readonly (string | number)[];
  onSelectionChange?: (keys: Array<string | number>, rows: T[]) => void;
  rowSelectionType?: "radio" | "checkbox";
  getCheckboxProps?: (record: T) => { disabled?: boolean };
  tableKey?: Key;
  persistenceKey?: string;
  tableLayout?: "auto" | "fixed";
  showToolbarOptions?: boolean;
};

function isFilterableColumn<T extends { id: number | string }>(column: ProColumns<T>) {
  return (
    column.filterDropdown !== undefined ||
    column.filters !== undefined ||
    column.onFilter !== undefined
  );
}

function readFilterKey<T extends { id: number | string }>(column: ProColumns<T>, index: number) {
  if (column.key !== undefined && column.key !== null) {
    return String(column.key);
  }
  if (Array.isArray(column.dataIndex)) {
    return column.dataIndex.map((segment) => String(segment)).join(".");
  }
  if (column.dataIndex !== undefined && column.dataIndex !== null) {
    return String(column.dataIndex);
  }
  return `__shared_asset_picker_filter_${index}`;
}

function normalizeFilterValue(value: unknown): ControlledFilterValue {
  if (!Array.isArray(value)) {
    return null;
  }
  const normalized = value.filter(
    (item): item is string | number | boolean =>
      typeof item === "string" || typeof item === "number" || typeof item === "boolean",
  );
  return normalized.length > 0 ? normalized : null;
}

function normalizeAllowedScopes(allowedScopes?: readonly SharedAssetPickerScope[]) {
  if (!allowedScopes) return SHARED_ASSET_PICKER_SCOPES;
  if (allowedScopes.length === 0) {
    throw new Error("SHARED_ASSET_PICKER_CONTRACT_INVALID: allowedScopes must not be empty");
  }
  for (const scope of allowedScopes) {
    if (!SHARED_ASSET_PICKER_SCOPES.includes(scope)) {
      throw new Error(`SHARED_ASSET_PICKER_CONTRACT_INVALID: unsupported scope ${scope}`);
    }
  }
  return allowedScopes;
}

function buildControlledFilters<T extends { id: number | string }>(
  columns: readonly ProColumns<T>[],
): ControlledFilters {
  return columns.reduce<ControlledFilters>((result, column, index) => {
    if (!isFilterableColumn(column)) {
      return result;
    }
    result[readFilterKey(column, index)] = normalizeFilterValue(column.filteredValue);
    return result;
  }, {});
}

export function SharedAssetPickerModal<T extends { id: number | string }>({
  open,
  actionRef,
  activeScope,
  allowedScopes,
  onScopeChange,
  scopeLabels,
  columns,
  request,
  onClose,
  title,
  header,
  footer,
  footerExtra,
  submitText,
  cancelText,
  onSubmit,
  width = "min(1240px, calc(100vw - 32px))",
  rowKey = "id",
  selectedRowKeys = [],
  onSelectionChange,
  rowSelectionType = "radio",
  getCheckboxProps,
  tableKey,
  persistenceKey,
  tableLayout = "auto",
  showToolbarOptions = true,
}: SharedAssetPickerModalProps<T>) {
  const [controlledFilters, setControlledFilters] = useState<ControlledFilters>(() =>
    buildControlledFilters(columns),
  );
  const [submitting, setSubmitting] = useState(false);
  const normalizedAllowedScopes = normalizeAllowedScopes(allowedScopes);

  const handleSubmit = useCallback(async () => {
    if (!onSubmit) {
      return false;
    }
    setSubmitting(true);
    try {
      return await onSubmit();
    } finally {
      setSubmitting(false);
    }
  }, [onSubmit]);
  const rowDoubleClick = usePickerRowDoubleClick({
    open,
    rowKey,
    rowSelectionType,
    selectedRowKeys,
    ...(onSelectionChange ? { onSelectionChange } : {}),
    ...(getCheckboxProps ? { getCheckboxProps } : {}),
    ...(onSubmit ? { onSubmit: handleSubmit } : {}),
    onClose,
    blocked: submitting,
  });

  useEffect(() => {
    setControlledFilters(buildControlledFilters(columns));
  }, [columns]);

  const normalizedColumns = useMemo<ProColumns<T>[]>(
    () =>
      columns.map((column, index): ProColumns<T> => {
        if (!isFilterableColumn(column)) {
          return column;
        }
        const filterKey = readFilterKey(column, index);
        return {
          ...column,
          filteredValue: controlledFilters[filterKey] ?? normalizeFilterValue(column.filteredValue),
        };
      }),
    [columns, controlledFilters],
  );

  const handleTableChange = useCallback(
    (_pagination: unknown, filters: Record<string, unknown> | undefined) => {
      setControlledFilters(() =>
        columns.reduce<ControlledFilters>((result, column, index) => {
          if (!isFilterableColumn(column)) {
            return result;
          }
          const filterKey = readFilterKey(column, index);
          result[filterKey] = normalizeFilterValue(filters?.[filterKey]);
          return result;
        }, {}),
      );
    },
    [columns],
  );

  const standardFooter = onSubmit ? (
    <Space style={{ marginTop: 16, width: "100%", justifyContent: "space-between" }}>
      <span>{footerExtra}</span>
      <Space>
        <Button onClick={onClose}>{cancelText}</Button>
        <Button
          color="primary"
          variant="solid"
          loading={submitting}
          onClick={async () => {
            const shouldClose = await handleSubmit();
            if (shouldClose) {
              onClose();
            }
          }}
        >
          {submitText}
        </Button>
      </Space>
    </Space>
  ) : undefined;
  const footerNode = footer ?? standardFooter;
  const tableNode = (
    <AdminPickerWorkspace<T>
      tableKey={tableKey ?? activeScope}
      header={header}
      columns={normalizedColumns}
      rowKey={rowKey}
      {...(persistenceKey === undefined ? {} : { persistenceKey })}
      showToolbarOptions={showToolbarOptions}
      tableLayout={tableLayout}
      footerRight={footerNode}
      selectedRowKeys={selectedRowKeys}
      {...(onSelectionChange ? { onSelectionChange } : {})}
      rowSelectionType={rowSelectionType}
      {...(getCheckboxProps ? { getCheckboxProps } : {})}
      tableProps={{
        actionRef,
        cardBordered: true,
        request,
        onChange: handleTableChange,
        pagination: ADMIN_LIST_PAGINATION_STANDARD,
        headerTitle: null,
        ...(rowDoubleClick.enabled
          ? {
              onRow: withPickerRowDoubleClick(undefined, rowDoubleClick.onRowDoubleClick),
            }
          : {}),
      }}
    />
  );

  return (
    <Modal title={title} open={open} width={width} onCancel={onClose} footer={null} destroyOnHidden>
      <Tabs
        activeKey={activeScope}
        tabPlacement="start"
        onChange={(key) => onScopeChange(key as SharedAssetPickerScope)}
        items={normalizedAllowedScopes.map((scope) => ({
          key: scope,
          label: scopeLabels[scope],
          children: scope === activeScope ? tableNode : null,
        }))}
      />
    </Modal>
  );
}
